import { Op } from "sharedb/lib/client";

import { BlockType } from "../../../shared/types/worksheet";
import {
  submitOpPromise,
  worksheetHasStudentResponseData,
} from "../../../shared/worksheets/doc-helpers";
import { showBlockDiffModal } from "../components/BlockDiffModal";
import { AIWorksheetOperation, worksheetSchema } from "../schemas/worksheet-schema";

import { computeWorksheetModifyOperations } from "./modify-worksheet-ops";
import { ToolType } from "./types";

import { t } from "i18n/i18n";
import { docCache } from "sharedb/ShareDBDoc";
import { store } from "store/index";
import { pageSelectors } from "store/selectors";
import { BROADCAST_TOPICS, sendBroadcast } from "utils/broadcast";

export const modifyWorksheet = async (params: { operations: AIWorksheetOperation[] }) => {
  const state = store.getState();
  const doc = docCache.current;
  if (!doc) {
    throw new Error("Worksheet for current Page not found");
  }
  const pageId = pageSelectors.selectAll(state).find((p) => p.worksheet_id === doc?.id)?.id;
  if (!pageId) {
    throw new Error("Current Page not found");
  }

  if (worksheetHasStudentResponseData(doc)) {
    return t("ai.worksheet_modify_blocked_student_data");
  }

  const { operations } = params;
  if (!operations?.length) {
    throw new Error("No operations to apply.");
  }

  let finalBlocks: BlockType[] = [];
  let ops: Op[] = [];
  try {
    ({ finalBlocks, ops } = computeWorksheetModifyOperations(doc, operations));
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return `Error: ${msg}`;
  }
  if (ops.length === 0) {
    return t("ai.worksheet_modify_no_changes");
  }

  const accepted = await showBlockDiffModal(doc, finalBlocks, pageId);
  if (!accepted) {
    return t("ai.modification_rejected");
  }

  await submitOpPromise(doc, ops);

  sendBroadcast(BROADCAST_TOPICS.AI_PAGE_ENHANCED, { pageId });

  return t("ai.modification_accepted");
};

export const modifyWorksheetTool: ToolType<{
  operations: AIWorksheetOperation[];
}> = {
  name: "modify_page_content",
  description: [
    "Edit the current Page worksheet: append new blocks, delete blocks by id, or patch instructor Text, choice labels, or Code cell starter source.",
    "patch_field: block_id = structure.block_id. field_id = VALUE of one of instructions_field_id, content_field_id, choice_field_id, or code_field_id (worksheet JSON). Set exactly one of text_markdown (Text), choice_labels (ChoiceField), or code_source (CodeField plain text).",
    "Operations are applied in order: all deletes, then all patches, then all appends (even if you list them differently).",
    "For new blocks only, use append with pseudo blocks (e.g. static_text with full markdown in `data` for read-only intro text; code with markdown `instructions` plus plain-text starter `data`).",
    "If the page context states that assistant worksheet edits are disabled (learner submission data present), do not call this tool; explain that limitation to the user.",
    "The user must accept a preview before changes are saved.",
  ].join("\n"),
  progressMessage: (p) => {
    const n = Array.isArray(p?.operations) ? p.operations.length : 0;
    if (n === 0) {
      return "Applying worksheet operations…";
    }
    return `Applying ${n} worksheet operation${n === 1 ? "" : "s"}…`;
  },
  parameters: worksheetSchema,
  callback: modifyWorksheet,
};
